export function getCategoryFromFolderType(folderType) {
  const mapping = {
    emails: 'email_attachments',
    messages: 'message_attachments',
    'text-messages': 'text_message_attachments',
    faxes: 'fax_attachments',
    events: 'event_attachments',
  };
  return mapping[folderType] || null;
}

const categories = ['emails', 'messages', 'text-messages', 'faxes', 'events'];

export function isVirtualCommunicationFolder(folder) {
  return folder?.is_virtual === true && categories.includes(folder?.folder_type);
}

/**
 * Builds the request params object for the file list API.
 * Mirrors the logic in FileManagerContext.handleGetFiles so React Query key and request stay in sync.
 *
 * @param {Object} options
 * @param {boolean} options.isGlobalFileManager
 * @param {boolean} options.isCaseFileManager
 * @param {boolean} options.isLeadFileManager
 * @param {boolean} options.isClientFileManager
 * @param {boolean} options.isFirmDocuments
 * @param {Object} options.fileManagerFilters
 * @param {Object} options.caseDetails
 * @param {Object} options.userContext
 * @param {string} options.query - search query
 * @param {string} [options.overrideFolderId] - optional folder id override for refetch (e.g. after create folder)
 * @param {string} [options.pagination_token]
 * @returns {Object} Params object for fetchFiles
 */
export function buildFileListParams({
  isGlobalFileManager,
  isCaseFileManager,
  isLeadFileManager,
  isClientFileManager,
  isFirmDocuments,
  fileManagerFilters = {},
  caseDetails,
  userContext,
  query = '',
  overrideFolderId,
  pagination_token,
}) {
  let case_id;
  let shouldIncludeCaseId = true;

  if (isFirmDocuments) {
    case_id = undefined;
    shouldIncludeCaseId = true;
  } else if (isGlobalFileManager) {
    if (fileManagerFilters?.uploadedBy === 'all' || !fileManagerFilters?.uploadedBy) {
      case_id = undefined;
      shouldIncludeCaseId = false;
    } else if (fileManagerFilters?.is_client_uploaded === true) {
      case_id = undefined;
      shouldIncludeCaseId = false;
    } else if (fileManagerFilters?.isFirmUploads) {
      case_id = undefined;
      shouldIncludeCaseId = true;
    } else if (fileManagerFilters?.forceCaseUndefined) {
      case_id = undefined;
      shouldIncludeCaseId = false;
    } else {
      case_id = fileManagerFilters?.case?.id;
      shouldIncludeCaseId = true;
    }
  } else if (isClientFileManager) {
    if (userContext?.clientCaseList?.length === 1) {
      case_id = userContext?.clientCaseList[0]?.case_id;
    } else {
      case_id = fileManagerFilters?.case?.id || userContext?.clientSelectedCase?.case_id;
    }
    shouldIncludeCaseId = true;
  } else {
    case_id = caseDetails?.case_id;
    shouldIncludeCaseId = true;
  }

  const selectedFolder = overrideFolderId ?? fileManagerFilters?.selectedFolder;

  const isVirtualFolder = isVirtualCommunicationFolder(fileManagerFilters) || null;
  const category = isVirtualFolder ? getCategoryFromFolderType(fileManagerFilters?.folder_type) : null;

  if (isVirtualFolder && !category) {
    console.error(`[FileManager] Unknown virtual folder type: ${fileManagerFilters?.folder_type}`);
  }

  const obj = {
    selectedFolder: isVirtualFolder ? '' : selectedFolder || '',
    selectedType: fileManagerFilters?.fileType || '',
    tag: fileManagerFilters?.tag || '',
    query: query || '',
    pagination_token: pagination_token ?? '',
    is_read: fileManagerFilters?.fileStatus ?? fileManagerFilters?.is_read ?? '',
  };

  if (category) {
    obj.category = category;
  }

  if (fileManagerFilters?.shared_folder) {
    obj.shared_folder = fileManagerFilters.shared_folder;
  }
  if (
    fileManagerFilters?.shared_contact_list &&
    Array.isArray(fileManagerFilters.shared_contact_list) &&
    fileManagerFilters.shared_contact_list.length > 0
  ) {
    obj.shared_contact_list = fileManagerFilters.shared_contact_list;
  }
  if (fileManagerFilters?.folder_shared === true) {
    obj.folder_shared = fileManagerFilters.folder_shared;
  }
  if (shouldIncludeCaseId && case_id) {
    obj.case_id = case_id;
  }

  if (isCaseFileManager || isClientFileManager || isLeadFileManager) {
    obj.is_read = fileManagerFilters?.fileStatus ?? '';
  } else {
    obj.is_read = fileManagerFilters?.is_read ?? '';
    obj.start_date = fileManagerFilters?.start_date ?? '';
    obj.end_date = fileManagerFilters?.end_date ?? '';
    if (fileManagerFilters?.is_client_uploaded === true) {
      obj.is_client_uploaded = true;
    } else if (fileManagerFilters?.isFirmUploads === true) {
      obj.is_client_uploaded = false;
    }
    obj.esignature_status = fileManagerFilters?.esignature_status ?? '';
  }

  return obj;
}
